import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, ChevronDown, Download } from 'lucide-react';
import {
  Badge,
  Button,
  ButtonGroup,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import {
  PARTNER_MATERIAL_LOCALES,
  type PartnerMaterial,
  type PartnerMaterialLocale
} from '@bliks-io/contracts';
import {
  PartnerFileFormatBadge,
  PartnerFileFormatIcon
} from '@/components/partner-content/PartnerFileFormat';
import { usePartnerMaterialDownload } from '@/pages/partner/api/mutations/usePartnerMaterials';
import {
  PARTNER_MATERIAL_CATEGORY_KEYS,
  PARTNER_MATERIAL_LOCALE_NAMES,
  downloadLocaleFor,
  isRecentlyChanged,
  pickPartnerMaterialText
} from '@/lib/partner-material-text';
import { formatFileSize } from '@/lib/formatters';

/** Language codes spelled out in the column before the rest is counted. */
const VISIBLE_LANGUAGES = 3;

/**
 * A material as the table shows it in the chosen language. The texts are row
 * data rather than accessor work: accessors stay pure, and search and
 * grouping read the language the partner reads.
 */
interface FileRow {
  material: PartnerMaterial;
  title: string;
  description: string;
  categoryLabel: string;
  languages: PartnerMaterialLocale[];
  /** The language a click downloads: the chosen one, else the fallback. */
  served: PartnerMaterialLocale;
  isNew: boolean;
}

interface InfoHubFilesTableProps {
  materials: PartnerMaterial[];
  locale: PartnerMaterialLocale;
  isLoading: boolean;
}

export function InfoHubFilesTable({ materials, locale, isLoading }: InfoHubFilesTableProps) {
  const { t } = useTranslation();

  const rows = useMemo<FileRow[]>(
    () =>
      materials.map((material) => {
        const languages = PARTNER_MATERIAL_LOCALES.filter((value) => material.files[value]);
        return {
          material,
          title: pickPartnerMaterialText(material.title, locale),
          description: pickPartnerMaterialText(material.description, locale),
          categoryLabel: t(PARTNER_MATERIAL_CATEGORY_KEYS[material.category]),
          languages,
          served: downloadLocaleFor(languages, locale),
          isNew: isRecentlyChanged(material.changedAt)
        };
      }),
    [materials, locale, t]
  );

  const columns: DataTableColumnDef<FileRow>[] = [
    {
      accessorKey: 'categoryLabel',
      meta: { label: t('partner.infoHub.category') },
      header: createColumnHeader(t('partner.infoHub.category'))
    },
    {
      accessorKey: 'title',
      header: createColumnHeader(t('partner.infoHub.columns.title')),
      cell: ({ row }) => <TitleCell row={row.original} />
    },
    {
      id: 'languages',
      meta: { fitContent: true },
      header: createColumnHeader(t('partner.infoHub.columns.languages')),
      cell: ({ row }) => <LanguagesCell row={row.original} />
    },
    {
      id: 'format',
      meta: { fitContent: true },
      header: createColumnHeader(t('partner.infoHub.columns.format')),
      cell: ({ row }) => {
        const file = row.original.material.files[row.original.served];
        return file ? (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <PartnerFileFormatBadge format={file.format} />
            <span className="text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
          </div>
        ) : null;
      }
    },
    {
      id: 'download',
      meta: { fitContent: true },
      header: () => <span className="sr-only">{t('partner.infoHub.download')}</span>,
      cell: ({ row }) => <DownloadButton row={row.original} locale={locale} />
    }
  ];

  return (
    <TooltipProvider delayDuration={200}>
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        enableSorting={false}
        enablePagination={false}
        enableGlobalFilter
        globalSearchPlaceholder={t('partner.infoHub.search')}
        emptyMessage={t('partner.infoHub.empty')}
        initialGrouping={['categoryLabel']}
      />
    </TooltipProvider>
  );
}

function TitleCell({ row }: { row: FileRow }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 items-start gap-3">
      <PartnerFileFormatIcon format={row.material.files[row.served]?.format} />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.title}</span>
          {row.isNew && (
            <Badge colorScheme="success" size="sm">
              {t('partner.infoHub.new')}
            </Badge>
          )}
        </div>
        {row.description && (
          <p className="line-clamp-1 text-xs text-muted-foreground">{row.description}</p>
        )}
      </div>
    </div>
  );
}

/**
 * The available languages as short codes, the first few spelled out and the
 * rest counted: a column of one pill per language would overflow as the
 * material grows. The tooltip names them all.
 */
function LanguagesCell({ row }: { row: FileRow }) {
  const { t } = useTranslation();
  const shown = row.languages.slice(0, VISIBLE_LANGUAGES);
  const hidden = row.languages.length - shown.length;
  const allNames = row.languages
    .map((language) => PARTNER_MATERIAL_LOCALE_NAMES[language])
    .join(', ');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          aria-label={t('partner.infoHub.availableIn', { languages: allNames })}
          className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-muted-foreground"
        >
          {shown.map((language) => language.toUpperCase()).join(' · ')}
          {hidden > 0 && (
            <Badge variant="outline" size="sm">
              {t('partner.infoHub.moreLanguages', { count: hidden })}
            </Badge>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>{allNames}</TooltipContent>
    </Tooltip>
  );
}

/**
 * The button names the language a click downloads; the menu beside it offers
 * every other one. When the partner's language is missing, the tooltip says
 * which version they get instead.
 */
function DownloadButton({ row, locale }: { row: FileRow; locale: PartnerMaterialLocale }) {
  const { t } = useTranslation();
  const download = usePartnerMaterialDownload();
  const { material, languages, title, served } = row;
  const isFallback = served !== locale;

  const start = (language: PartnerMaterialLocale) =>
    download.mutate(
      { id: material._id, locale: language },
      {
        onSuccess: ({ url }) => {
          window.location.href = url;
        },
        onError: () => toast.error(t('partner.infoHub.downloadFailed'))
      }
    );

  const mainButton = (
    <Button
      variant="outline"
      size="small"
      loading={download.isPending}
      disabled={download.isPending}
      aria-label={t('partner.infoHub.downloadTitle', {
        title,
        language: PARTNER_MATERIAL_LOCALE_NAMES[served]
      })}
      onClick={() => start(served)}
    >
      <Download className="mr-2 h-4 w-4" />
      {t('partner.infoHub.download')}
      <span className="ml-2 text-xs text-muted-foreground">{served.toUpperCase()}</span>
    </Button>
  );

  return (
    <ButtonGroup>
      {isFallback ? (
        <Tooltip>
          <TooltipTrigger asChild>{mainButton}</TooltipTrigger>
          <TooltipContent>
            {t('partner.infoHub.fallbackHint', {
              wanted: PARTNER_MATERIAL_LOCALE_NAMES[locale],
              language: PARTNER_MATERIAL_LOCALE_NAMES[served]
            })}
          </TooltipContent>
        </Tooltip>
      ) : (
        mainButton
      )}
      {languages.length > 1 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="small"
              disabled={download.isPending}
              aria-label={t('partner.infoHub.otherLanguages', { title })}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('partner.infoHub.downloadIn')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {languages.map((language) => {
              const file = material.files[language];
              return (
                <DropdownMenuItem key={language} onSelect={() => start(language)}>
                  <Check
                    className={cn('h-4 w-4', language === served ? 'opacity-100' : 'opacity-0')}
                    aria-hidden
                  />
                  <span className="flex-1">{PARTNER_MATERIAL_LOCALE_NAMES[language]}</span>
                  {file && (
                    <span className="ml-4 text-xs text-muted-foreground">
                      {file.format.toUpperCase()} · {formatFileSize(file.size)}
                    </span>
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </ButtonGroup>
  );
}
